import {
	useState,
	useRef,
	useEffect,
	useLayoutEffect,
	useMemo
} from 'react';
import { createPortal } from 'react-dom';
import { useDatePicker } from '@rehookify/datepicker';
import type { DPDay } from '@rehookify/datepicker';
import format from 'date-fns/format';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import CalendarIcon from '@mui/icons-material/TodayOutlined';
import ArrowBackIcon from '@mui/icons-material/ChevronLeft';
import ArrowForwardIcon from '@mui/icons-material/ChevronRight';
import { Button } from '../Button/Button';
import { Text, Caption } from '../../CORE/Text/Text';
import { BUTTON_TYPES, BUTTON_SIZE, TDateRange, TDateRangePreset } from '@types';
import { DEFAULT_PRESETS } from './DateRangePicker.presets';
import styles from './DateRangePicker.module.scss';

export interface IDateRangePickerProps {
	value: TDateRange;
	onChange: (range: TDateRange) => void;
	/** Optional left-sidebar quick options. Omit to show no presets. */
	presets?: TDateRangePreset[];
	/** Day index that starts the week (0 = Sunday, 1 = Monday, ...). Defaults to 0. */
	weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
	displayThin?: boolean;
}

const formatTrigger = (start: Date, end: Date) =>
	`${format(start, 'MMM d, yyyy')} — ${format(end, 'MMM d, yyyy')}`;

const formatInput = (d: Date) => format(d, 'dd/MM/yyyy');

const isSameDay = (a: Date, b: Date) =>
	a.getFullYear() === b.getFullYear()
	&& a.getMonth() === b.getMonth()
	&& a.getDate() === b.getDate();

const isSameRange = (a: TDateRange, b: TDateRange) =>
	isSameDay(a.start, b.start) && isSameDay(a.end, b.end);

const sortDates = (dates: Date[]): Date[] => {
	if (dates.length < 2) return dates;
	const [a, b] = dates;
	return a.getTime() <= b.getTime() ? [a, b] : [b, a];
}

/*
	Resolve the staged selection into a range, normalised to start/end of day
	so a single-day pick is a full inclusive day.
*/
const resolveStagedRange = (dates: Date[]): TDateRange | null => {
	if (dates.length === 0) return null;
	if (dates.length === 1) return { start: startOfDay(dates[0]), end: endOfDay(dates[0]) };
	const [start, end] = sortDates(dates);
	return { start: startOfDay(start), end: endOfDay(end) };
}

export const DateRangePicker = ({
	value,
	onChange,
	presets = DEFAULT_PRESETS,
	weekStartsOn = 0,
	displayThin = false,
}: IDateRangePickerProps) => {
	const [open, setOpen] = useState(false);
	const [staged, setStaged] = useState<Date[]>([value.start, value.end]);
	const [offsetDate, setOffsetDate] = useState<Date>(value.start);
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);

	/*
		The popover is portalled to the body so it escapes any ancestor overflow/transform
		clip (e.g. the filter's FloatingMenu). Anchor it below the trigger, right-aligned,
		then clamp every edge inside the viewport so it can never fall off screen.
	*/
	const computePosition = () => {
		const trigger = containerRef.current;
		const popover = popoverRef.current;
		if (!trigger || !popover) return;
		const margin = 8;
		const gap = 6;
		const anchor = trigger.getBoundingClientRect();
		const { width, height } = popover.getBoundingClientRect();
		const { innerWidth, innerHeight } = window;
		let top = anchor.bottom + gap;
		if (top + height > innerHeight - margin && anchor.top - gap - height >= margin) {
			top = anchor.top - gap - height;
		}
		let left = anchor.right - width;
		left = Math.min(Math.max(left, margin), innerWidth - width - margin);
		top = Math.min(Math.max(top, margin), innerHeight - height - margin);
		setPos({ top, left });
	}

	useEffect(() => {
		if (open) {
			setStaged([value.start, value.end]);
			setOffsetDate(value.start);
		}
	}, [open, value]);

	useEffect(() => {
		if (!open) return;
		const onClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			const inTrigger = containerRef.current?.contains(target);
			const inPopover = popoverRef.current?.contains(target);
			if (!inTrigger && !inPopover) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', onClickOutside);
		return () => document.removeEventListener('mousedown', onClickOutside);
	}, [open]);

	useLayoutEffect(() => {
		if (!open) return;
		computePosition();
		window.addEventListener('resize', computePosition);
		window.addEventListener('scroll', computePosition, true);
		return () => {
			window.removeEventListener('resize', computePosition);
			window.removeEventListener('scroll', computePosition, true);
		};
	}, [open]);

	const {
		data: { calendars, weekDays },
		propGetters: { dayButton, addOffset, subtractOffset },
	} = useDatePicker({
		selectedDates: staged,
		onDatesChange: setStaged,
		offsetDate,
		onOffsetChange: setOffsetDate,
		dates: { mode: 'range' },
		// rehookify auto-prepends [0] for the main calendar, so [1] yields 2 calendars (current + next month).
		calendar: { offsets: [1], startDay: weekStartsOn },
	});

	const activePresetKey = useMemo(() => {
		const range = resolveStagedRange(staged);
		if (!range) return undefined;
		const today = new Date();
		return presets.find((p) => isSameRange(p.resolve(today), range))?.key;
	}, [staged, presets]);

	const applyPreset = (p: TDateRangePreset) => {
		const next = p.resolve(new Date());
		setStaged([next.start, next.end]);
		setOffsetDate(next.start);
	}

	const onSave = () => {
		const range = resolveStagedRange(staged);
		if (!range) return;
		onChange(range);
		setOpen(false);
	}

	const onCancel = () => {
		setStaged([value.start, value.end]);
		setOffsetDate(value.start);
		setOpen(false);
	}

	const stagedRange = resolveStagedRange(staged);
	const canSave = stagedRange !== null;

	const dayClass = (day: DPDay) => {
		const classes = [styles.day];
		if (!day.inCurrentMonth) classes.push(styles.dayOutside);
		if (day.disabled) classes.push(styles.dayDisabled);
		if (day.range === 'range-start') classes.push(styles.dayRangeStart);
		if (day.range === 'range-end') classes.push(styles.dayRangeEnd);
		if (day.range === 'range-start range-end') classes.push(styles.dayRangeStart, styles.dayRangeEnd);
		if (day.range === 'in-range') classes.push(styles.dayInRange);
		if (day.selected) classes.push(styles.daySelected);
		if (day.now) classes.push(styles.dayToday);
		return classes.join(' ');
	}

	return (
		<div className={styles.container} ref={containerRef}>
			<button
				type={'button'}
				className={`${styles.trigger} ${displayThin ? styles.displayThin : ''} ${open ? styles.triggerOpen : ''}`}
				onClick={() => setOpen((o) => !o)}
				aria-haspopup={'dialog'}
				aria-expanded={open}
			>
				<CalendarIcon className={styles.triggerIcon} />
				<Text>{formatTrigger(value.start, value.end)}</Text>
			</button>

			{open && createPortal(
				<div
					className={styles.popover}
					role={'dialog'}
					ref={popoverRef}
					style={pos ? { top: pos.top, left: pos.left } : { opacity: 0 }}
				>
					<div className={styles.body}>
						{presets.length > 0 && (
							<ul className={styles.presets}>
								{presets.map((p) => (
									<li key={p.key}>
										<button
											type={'button'}
											className={`${styles.preset} ${activePresetKey === p.key ? styles.presetActive : ''}`}
											onClick={() => applyPreset(p)}
										>
											{p.label}
										</button>
									</li>
								))}
							</ul>
						)}

						<div className={styles.calendars}>
							<div className={styles.calNav}>
								<button type={'button'} className={styles.navBtn} {...subtractOffset({ months: 1 })} aria-label={'Previous month'}>
									<ArrowBackIcon />
								</button>
								<div className={styles.navSpacer} />
								<button type={'button'} className={styles.navBtn} {...addOffset({ months: 1 })} aria-label={'Next month'}>
									<ArrowForwardIcon />
								</button>
							</div>

							<div className={styles.calGrid}>
								{calendars.map((cal, idx) => (
									<div key={idx} className={styles.cal}>
										<div className={styles.calTitle}>{`${cal.month} ${cal.year}`}</div>
										<div className={styles.weekDays}>
											{weekDays.map((w, i) => (
												<Caption key={i} text={w} />
											))}
										</div>
										<div className={styles.daysGrid}>
											{cal.days.map((day) => (
												<button
													key={day.$date.toString()}
													type={'button'}
													className={dayClass(day)}
													disabled={day.disabled}
													{...dayButton(day)}
												>
													<span className={styles.dayInner}>{day.day}</span>
												</button>
											))}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>

					<div className={styles.footer}>
						<div className={styles.inputs}>
							<div className={styles.dateInput}>
								<span>{stagedRange ? formatInput(stagedRange.start) : ''}</span>
								<CalendarIcon className={styles.dateInputIcon} />
							</div>
							<span className={styles.inputDash}>—</span>
							<div className={styles.dateInput}>
								<span>{stagedRange ? formatInput(stagedRange.end) : ''}</span>
								<CalendarIcon className={styles.dateInputIcon} />
							</div>
						</div>
						<div className={styles.actions}>
							<Button
								text={'Cancel'}
								type={BUTTON_TYPES.SECONDARY}
								size={BUTTON_SIZE.SMALL}
								onClick={onCancel}
							/>
							<Button
								text={'Save'}
								type={BUTTON_TYPES.PRIMARY}
								size={BUTTON_SIZE.SMALL}
								onClick={onSave}
								disabled={!canSave}
							/>
						</div>
					</div>
				</div>,
				document.body,
			)}
		</div>
	);
}
